import React, { useState, useRef } from "react";
import { ChatWidget } from "~components";
import cn from "classnames";
import { useInterval } from "~hooks";
import * as styles from "./styles.module.scss";

/** ============================================================================
 * @component
 * Receieves array of chat widgets and orchestrates the animation
 *
 * @param 	{node[]}  		messages			 Array of ChatWidget components
 * @param 	{boolean}  		isAtfInView	 	 Whether or not the ATF is in view, and we can transition to next slide
 * @param 	{function}  	goToNextSlide	 Go to the next ATF slide
 * @return	{node}
 */

const ChatAnimation = ({ messages = [], isAtfInView, goToNextSlide }) => {
  const [thread, setThread] = useState([]);

  const unsentMessages = useRef(messages);

  const ANIMATION_SPEED = 1500;

  const isTypingRef = useRef(false);

  const updateThread = () => {
    // Logic for displaying the typing bubble
    if (unsentMessages.current[0].props.sender === `incoming`) {
      if (isTypingRef.current) {
        isTypingRef.current = false;
        setThread((prev) => [
          ...prev.slice(0, prev.length - 1),
          unsentMessages.current.shift()
        ]);
      } else {
        isTypingRef.current = true;
        setThread((prev) => [
          ...prev,
          <ChatWidget type="typing" sender="incoming" />
        ]);
      }
    } else {
      setThread((prev) => [...prev, unsentMessages.current.shift()]);
    }
  };

  let beatsSkipped = 0;
  const BEATS_TO_SKIP_BEFORE_PROGRESSING_SLIDE = 1;

  useInterval(() => {
    const hasSkippedSufficientBeats =
      beatsSkipped === BEATS_TO_SKIP_BEFORE_PROGRESSING_SLIDE;

    if (unsentMessages.current.length > 0) {
      updateThread();
      return;
    }

    if (!isAtfInView) {
      return;
    }

    if (hasSkippedSufficientBeats) {
      beatsSkipped = 0;
      goToNextSlide();
    } else {
      beatsSkipped += 1;
    }
  }, ANIMATION_SPEED);

  return (
    <div className={styles.threadContainer}>
      {thread.map((message, i) => (
        <div
          key={`${i}${message.props.type}`}
          className={cn(styles.container, {
            [styles.alignRight]:
              message.props.sender === `outgoing` ||
              message.props.type === `nextButton`
          })}
        >
          <div
            className={cn(styles.message, {
              [styles.fullWidth]:
                message.props.type !== `message` &&
                message.props.type !== `typing` &&
                message.props.type !== `nextButton`,
              [styles[message.props.sender]]: message.props.sender
            })}
          >
            {message}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChatAnimation;
